/**
 * CurrentPlanCard — shows current subscription state for shop owner:
 *   • Plan badge with limits + features
 *   • Trial / expiry countdown (red <0 days, amber <=5, slate otherwise)
 *   • Usage bars (customers, active loans) with X/Y + % progress
 *   • Renew / Upgrade CTAs
 *
 * Used on Subscription page (full) and as a compact widget in Settings.
 */
import React, { useEffect, useState } from "react";
import { api, fmtPKR } from "../lib/api";
import { Sparkles, Clock, AlertCircle, CheckCircle2, ArrowRight, Crown } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";

const CYCLE_LABEL = {
  quarter: "/quarter",
  year: "/year",
  month: "/month",
  trial: "",
};

function Bar({ used, limit, label, testid }) {
  const cap = limit && limit > 0 ? limit : null;
  const pct = cap ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const danger = cap && pct >= 90;
  const warn = cap && pct >= 70 && pct < 90;
  const tone = danger ? "bg-red-500" : warn ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-semibold text-slate-700">{label}</span>
        <span className={`tabular font-bold ${danger ? "text-red-600" : "text-slate-600"}`}>
          {used} {cap ? `/ ${cap}` : "/ ∞"}
          {cap && <span className="ml-1 text-slate-400">({pct}%)</span>}
        </span>
      </div>
      {cap ? (
        <div className="h-1.5 w-full bg-slate-100 rounded-full overflow-hidden">
          <div className={`h-full ${tone} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      ) : (
        <div className="h-1.5 w-full bg-emerald-100 rounded-full" />
      )}
    </div>
  );
}

export default function CurrentPlanCard({ compact = false }) {
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    let active = true;
    Promise.all([
      api.get("/usage/me").then((r) => r.data),
      api.get("/platform/plans").then((r) => r.data),
    ])
      .then(([u, p]) => { if (active) { setUsage(u); setPlans(p); } })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  if (!usage) {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 p-5 animate-pulse" data-testid="current-plan-loading">
        <div className="h-4 w-32 bg-slate-200 rounded mb-3" />
        <div className="h-2 w-full bg-slate-200 rounded" />
      </div>
    );
  }

  const currentPlan = plans.find((p) => p.id === usage.plan_id) || {
    id: usage.plan_id, name: usage.plan_id, price_pkr: 0, features: [],
    billing_cycle: usage.billing_cycle,
  };
  const daysLeft = usage.days_left;
  const expired = !!usage.expired;
  const trialEndingSoon = daysLeft !== null && daysLeft >= 0 && daysLeft <= 5;
  const isFounding = !!usage.is_founding_member;
  const isTrial = usage.plan_id === "trial";

  // Founding takes precedence over expiring warning visually
  const headerTone = isFounding && !expired
    ? "bg-gradient-to-r from-amber-50 to-amber-100 border-amber-300 text-amber-900"
    : expired
    ? "bg-red-50 border-red-300 text-red-800"
    : trialEndingSoon
    ? "bg-amber-50 border-amber-300 text-amber-800"
    : "bg-emerald-50 border-emerald-200 text-emerald-800";

  const HeaderIcon = isFounding ? Crown : expired ? AlertCircle : trialEndingSoon ? Clock : Sparkles;
  const cycleLabel = CYCLE_LABEL[currentPlan.billing_cycle || usage.billing_cycle] || "";

  return (
    <div className="rounded-md border border-slate-200 bg-white overflow-hidden" data-testid="current-plan-card">
      <div className={`border-b ${headerTone} px-5 py-4 flex items-center gap-3`}>
        <HeaderIcon className="w-5 h-5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider font-bold opacity-70">
            {isFounding ? `Founding Member #${usage.founder_no}` : "Active Plan"}
          </div>
          <div className="font-display font-bold text-lg leading-tight truncate" data-testid="current-plan-name">
            {currentPlan.name}
            {currentPlan.price_pkr ? (
              <span className="opacity-70 text-base font-normal"> — {fmtPKR(currentPlan.price_pkr)}{cycleLabel}</span>
            ) : null}
          </div>
        </div>
        {isFounding && (
          <span className="shrink-0 text-[10px] font-bold uppercase rounded-full px-2 py-0.5 bg-amber-600 text-white" data-testid="founding-badge">
            🏆 Founding
          </span>
        )}
        {usage.status === "suspended" && (
          <span className="shrink-0 text-[10px] font-bold uppercase rounded-full px-2 py-0.5 bg-red-600 text-white">
            Suspended
          </span>
        )}
      </div>

      <div className="px-5 py-4 space-y-4">
        {/* Expiry countdown */}
        {daysLeft !== null && (
          <div className="flex items-center gap-2 text-sm" data-testid="current-plan-trial-text">
            <Clock className={`w-4 h-4 ${expired ? "text-red-600" : trialEndingSoon ? "text-amber-600" : "text-slate-400"}`} />
            {expired ? (
              <span className="text-red-700 font-semibold">
                {isTrial ? "Trial khatam ho gaya — plan select karein" : "Subscription expired — please renew"}
              </span>
            ) : (
              <span className="text-slate-700">
                {isTrial ? "Trial mein" : "Subscription mein"}{" "}
                <span className={`font-bold ${trialEndingSoon ? "text-amber-700" : "text-slate-900"}`}>
                  {daysLeft} {daysLeft === 1 ? "day" : "days"}
                </span> baqi
                {usage.trial_ends && (
                  <span className="text-xs text-slate-400 ml-1">
                    (expires {String(usage.trial_ends).slice(0, 10)})
                  </span>
                )}
              </span>
            )}
          </div>
        )}

        {/* Usage bars — only show when limits exist (trial); hide for unlimited paid plans */}
        {(usage.max_customers != null || usage.max_active_loans != null) && (
          <div className="space-y-3">
            {usage.max_customers != null && (
              <Bar
                used={usage.customers_used}
                limit={usage.max_customers}
                label="Customers"
                testid="current-plan-bar-customers"
              />
            )}
            {usage.max_active_loans != null && (
              <Bar
                used={usage.active_loans_used}
                limit={usage.max_active_loans}
                label="Active loans"
                testid="current-plan-bar-loans"
              />
            )}
          </div>
        )}

        {/* Features chip strip (non-compact only) */}
        {!compact && (currentPlan.features || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1" data-testid="current-plan-features">
            {currentPlan.features.map((f) => (
              <span key={f} className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                <CheckCircle2 className="w-3 h-3" /> {f}
              </span>
            ))}
          </div>
        )}

        {/* CTA */}
        {(expired || trialEndingSoon || isTrial) ? (
          <Link to="/subscription" className="block">
            <Button
              className="w-full bg-emerald-600 hover:bg-emerald-700"
              data-testid="current-plan-upgrade-btn"
            >
              {expired ? "Plan Renew Karein" : isTrial ? "Upgrade to Founding Member" : "Renew Now"}
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
        ) : (
          <Link
            to="/subscription"
            className="block text-sm text-emerald-700 font-semibold hover:underline"
            data-testid="current-plan-manage-link"
          >
            Manage subscription →
          </Link>
        )}
      </div>
    </div>
  );
}
